import { Button, Flex, Spacer } from "@chakra-ui/react";
import React from "react";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import * as FSQ from "src/generated/hooks/form-service/queries";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { JsonEditor } from "src/scenes/admin/components/JsonEditor";
import {
  buildRuleImportErrors,
  partitionRulesForImport,
  RuleImportError,
} from "./helpers";
import { RuleImportExportJsonSchema } from "./ruleSchema";

type Props = {
  formTemplateId: string;
  onBack: () => void;
  onComplete: (errors: RuleImportError[]) => void;
};

export const ImportRulesFromJSON: React.FC<Props> = ({
  formTemplateId,
  onBack,
  onComplete,
}) => {
  const toast = useAvelaToast();
  const [json, setJson] = React.useState("");
  const [isImporting, setImporting] = React.useState(false);
  const service = FSQ.useImportRuleConfiguration();

  const handleImport = React.useCallback(async () => {
    setImporting(true);
    toast.closeAll();

    try {
      if (json.trim() === "") {
        toast.error({
          title: "JSON is required",
          description: "Please provide valid JSON to import",
        });
        return;
      }

      let parsed: unknown;
      try {
        parsed = JSON.parse(json);
      } catch {
        toast.error({
          title: "Invalid JSON",
          description: "Please check your JSON format",
        });
        return;
      }

      if (!Array.isArray(parsed)) {
        toast.error({
          title: "Invalid JSON",
          description: "Expected an array of rules",
        });
        return;
      }

      const warn = () =>
        toast({
          title: "Some rules were not imported",
          description: "Review the specific errors and try again",
          status: "warning",
        });

      const { validRules, parseErrors, crossRuleErrors } =
        partitionRulesForImport(parsed);

      if (crossRuleErrors.length > 0) {
        warn();
        return onComplete([...parseErrors, ...crossRuleErrors]);
      }

      if (validRules.length === 0) {
        warn();
        return onComplete(parseErrors);
      }

      const { data: result } = await service.mutateAsync({
        path: { formTemplateId },
        body: validRules as FSR.ImportRuleConfigurationData["body"],
      });
      const response = result?.data;

      if (!response) {
        toast.error({
          title: "Something went wrong",
          description: "Please try again",
        });
        return;
      }

      if (response.result === "invalid") {
        warn();
        return onComplete([
          ...parseErrors,
          ...buildRuleImportErrors(response.errorGroups, validRules),
        ]);
      }

      if (parseErrors.length > 0) {
        warn();
        return onComplete(parseErrors);
      }

      toast({
        title: "Imported from JSON",
        description: "Rules imported successfully",
        status: "info",
        isClosable: true,
      });
      return onComplete([]);
    } catch (error: any) {
      if (error?.error?.message) {
        toast.error({
          title: "Something went wrong",
          description: error.error.message,
        });
      } else {
        toast.genericError();
      }
    } finally {
      setImporting(false);
    }
  }, [json, service, formTemplateId, toast, onComplete]);

  return (
    <Flex direction="column" gap={6} grow="1" height="100%">
      <JsonEditor
        schema={RuleImportExportJsonSchema}
        text={json}
        onChange={setJson}
        filename="ruleConfiguration.json"
        editorHeight="fillParent"
      />
      <AdminFormButtons insideTabPanel>
        <Button variant="outline" colorScheme="gray" onClick={onBack}>
          Back
        </Button>
        <Spacer />
        <Button isLoading={isImporting} onClick={handleImport}>
          Import
        </Button>
      </AdminFormButtons>
    </Flex>
  );
};
